import type { ComponentType } from "react";
import { Image as ImageIcon, Play } from "lucide-react";
import { useInViewport } from "~/shared/hooks/useInViewport";
import { cn } from "~/shared/lib/utils";

export interface MediaThumbnailProps {
    /** Image URL; when null/undefined a placeholder with the fallback icon is shown. */
    src?: string | null;
    alt?: string;
    /** Fallback icon rendered when there is no `src`. */
    icon?: ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
    /** Sizing / radius — the caller decides (e.g. `aspect-[4/3] w-full` or `h-10 w-14 rounded-md`). */
    className?: string;
    /** Fallback icon size class. */
    iconClassName?: string;
}

/**
 * Non-interactive media preview slot: renders a cover image or, when no `src`,
 * a consistent dark placeholder with a centered icon. Image and placeholder share
 * the same box, so they stay identical in size. Mirrors the chat-card poster
 * (`~/pages/chats/chat-card.tsx`) so scenes — and later shots — reuse one slot.
 */
export function MediaThumbnail({
    src,
    alt = "",
    icon: Icon = ImageIcon,
    className,
    iconClassName = "size-8",
}: MediaThumbnailProps) {
    return (
        <div className={cn("relative overflow-hidden bg-muted", className)}>
            {src ? (
                <img src={src} alt={alt} loading="lazy" className="absolute inset-0 size-full object-cover" />
            ) : (
                <div className="absolute inset-0 flex items-center justify-center text-muted-foreground/40">
                    <Icon className={iconClassName} aria-hidden />
                </div>
            )}
        </div>
    );
}

export interface VideoThumbnailProps {
    /** Video URL; the first frame is painted as a poster. */
    src: string;
    alt?: string;
    /** Sizing / radius — the caller decides (same box as `MediaThumbnail`). */
    className?: string;
    /** Play-badge size class. */
    badgeClassName?: string;
}

/**
 * Video preview slot: paints the first frame as a poster via the `#t` media
 * fragment (no playback, no separate thumbnail asset — see
 * feedback_video_thumbnail_media_fragment) with a centered play badge. Shares
 * the box shape with `MediaThumbnail` so image / video / audio previews stay
 * identical in size.
 */
export function VideoThumbnail({ src, alt = "", className, badgeClassName = "size-5" }: VideoThumbnailProps) {
    // Первый кадр (через `#t=0.1`) грузим лениво — только когда постер близок к
    // кадру, иначе десятки роликов тянутся разом. Место держит бокс caller'а.
    const { ref, isInView } = useInViewport<HTMLDivElement>();
    return (
        <div ref={ref} className={cn("relative overflow-hidden bg-muted", className)}>
            {isInView && (
                <video
                    src={`${src}#t=0.1`}
                    muted
                    playsInline
                    preload="metadata"
                    aria-label={alt}
                    className="absolute inset-0 size-full object-cover"
                />
            )}
            <div className="absolute inset-0 flex items-center justify-center">
                <span
                    className={cn(
                        "flex items-center justify-center rounded-full bg-black/50 text-white",
                        badgeClassName,
                    )}
                >
                    <Play className="size-3 translate-x-px" aria-hidden />
                </span>
            </div>
        </div>
    );
}
